import { useEffect, useState } from "react";
import { getCameraStatus } from "services/cameraApi";
import { useStableState } from "utils/stableState";

// Polls the standalone camera's status (padihat-backend's camera-status
// endpoint). Faster than the Experiment page's 15s POLL_MS on purpose: an
// always-on camera polls the backend every few seconds and the backend calls
// it "online" only within ~45s of its last contact, so a 15s refresh would
// make that indicator lag noticeably behind reality.
export const CAMERA_STATUS_POLL_MS = 8000;

export function useCameraDeviceStatus(mac, intervalMs = CAMERA_STATUS_POLL_MS) {
  const [status, setStatus] = useStableState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!mac) {
      setStatus(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    // Drop a previous device's data immediately so switching cameras never
    // shows the old one's status under the new one's name.
    setStatus(null);
    setError(null);

    // `loading` means the FIRST load only. Flipping it on every background poll re-rendered the whole
    // page (charts included) twice per poll for nothing.
    let firstLoad = true;
    let showingError = false;
    const load = async () => {
      if (firstLoad) setLoading(true);
      try {
        const data = await getCameraStatus(mac);
        if (!cancelled) {
          // Same status as last time -> same object, so nothing downstream re-renders.
          setStatus(data);
          if (showingError) {
            showingError = false;
            setError(null);
          }
        }
      } catch (requestError) {
        showingError = true;
        if (!cancelled) setError(requestError.message || "Failed to load camera status");
      } finally {
        // Only the first load turned it on, so only it turns it off (a no-op set still costs a render).
        if (firstLoad && !cancelled) setLoading(false);
        firstLoad = false;
      }
    };

    load();
    const timer = window.setInterval(load, intervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [mac, intervalMs, setStatus]);

  return { status, error, loading };
}
